import * as Cesium from "cesium";

export function runFindClosePoints(dataSource, viewer, toggle, options) {
  // toggle off: remove primitive of this dataSource only
  if (!toggle) {
    removeFindClosePointsPrimitive(viewer, dataSource);
    return;
  }

  const entities = dataSource.entities.values;

  const distanceThresholdMeters = options && options.distanceThresholdMeters != null ? options.distanceThresholdMeters : 0.05; // 5 cm

  // min distance only for comparisons between different entities
  const minInterEntityDistanceMeters = options && options.minInterEntityDistanceMeters != null ? options.minInterEntityDistanceMeters : 0.005; // 5 mm

  const closePoints = []; // { x,y,z }
  const endpoints = []; // { x,y,z, entityId }

  function distance2D(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  let processedLines = 0;
  const batchSize = 500;
  const linesArray = Array.from(entities);

  function processBatch() {
    const end = Math.min(processedLines + batchSize, linesArray.length);

    for (let li = processedLines; li < end; li++) {
      const entity = linesArray[li];
      const polyline = entity.polyline;
      if (!polyline || !polyline.positions) continue;

      let positions;
      try {
        const now = Cesium.JulianDate.now();
        const value = polyline.positions.getValue ? polyline.positions.getValue(now) : polyline.positions;
        if (!value || !Array.isArray(value) || value.length < 2) continue;
        positions = value;
      } catch (e) {
        continue;
      }

      // collect endpoints for this entity
      const first = positions[0];
      const last = positions[positions.length - 1];
      endpoints.push({
        x: first.x,
        y: first.y,
        z: first.z,
        entityId: entity.id,
      });
      endpoints.push({
        x: last.x,
        y: last.y,
        z: last.z,
        entityId: entity.id,
      });

      // compare consecutive points inside this polyline (no min distance here)
      for (let i = 0; i < positions.length - 1; i++) {
        const p1 = positions[i];
        const p2 = positions[i + 1];

        if (distance2D(p1, p2) <= distanceThresholdMeters) {
          closePoints.push({ x: p1.x, y: p1.y, z: p1.z });
          closePoints.push({ x: p2.x, y: p2.y, z: p2.z });
        }
      }
    }

    processedLines = end;

    if (processedLines < linesArray.length) {
      setTimeout(processBatch, 0);
    } else {
      finalize();
    }
  }

  function finalize() {
    // compare endpoints between different entities only
    for (let i = 0; i < endpoints.length - 1; i++) {
      const p1 = endpoints[i];
      for (let j = i + 1; j < endpoints.length; j++) {
        const p2 = endpoints[j];

        // skip same entity, we only want inter-entity comparison
        if (p1.entityId === p2.entityId) continue;

        const d = distance2D(p1, p2);

        // must be within [minInterEntityDistanceMeters, distanceThresholdMeters]
        if (d <= distanceThresholdMeters && d >= minInterEntityDistanceMeters) {
          closePoints.push({ x: p1.x, y: p1.y, z: p1.z });
          closePoints.push({ x: p2.x, y: p2.y, z: p2.z });
        }
      }
    }

    const dedupePrecision = options && options.dedupePrecision != null ? options.dedupePrecision : 3;
    const map = new Map();

    for (let i = 0; i < closePoints.length; i++) {
      const p = closePoints[i];
      const key = [p.x.toFixed(dedupePrecision), p.y.toFixed(dedupePrecision), p.z.toFixed(dedupePrecision)].join(",");
      if (!map.has(key)) {
        map.set(key, p);
      }
    }

    let uniquePoints = Array.from(map.values());

    const maxPoints = options && options.maxPoints != null ? options.maxPoints : 50000;
    if (uniquePoints.length > maxPoints) {
      const step = Math.ceil(uniquePoints.length / maxPoints);
      const sampled = [];
      for (let i = 0; i < uniquePoints.length; i += step) {
        sampled.push(uniquePoints[i]);
      }
      uniquePoints = sampled;
    }

    // remove old primitive only for this dataSource
    removeFindClosePointsPrimitive(viewer, dataSource);

    // get or create map on viewer
    const dsMap = getFindClosePointsMap(viewer);

    const collection = viewer.scene.primitives.add(new Cesium.PointPrimitiveCollection());

    // store by dataSource
    dsMap.set(dataSource, collection);

    const addBatchSize = options && options.addBatchSize != null ? options.addBatchSize : 5000;
    let idx = 0;

    function addBatch() {
      const end = Math.min(idx + addBatchSize, uniquePoints.length);

      for (let i = idx; i < end; i++) {
        const p = uniquePoints[i];
        collection.add({
          position: new Cesium.Cartesian3(p.x, p.y, p.z),
          pixelSize: 6,
          color: Cesium.Color.RED,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 1,
        });
      }

      idx = end;

      if (idx < uniquePoints.length) {
        setTimeout(addBatch, 0);
      }
    }

    addBatch();
  }

  processBatch();
}

// helper for map of dataSource -> primitive
function getFindClosePointsMap(viewer) {
  if (!viewer._findClosePointsPrimitives) {
    viewer._findClosePointsPrimitives = new Map();
  }
  return viewer._findClosePointsPrimitives;
}

// remove primitive only for given dataSource
export function removeFindClosePointsPrimitive(viewer, dataSource) {
  const dsMap = viewer._findClosePointsPrimitives;
  if (!dsMap) return;

  const prim = dsMap.get(dataSource);
  if (prim) {
    viewer.scene.primitives.remove(prim);
    dsMap.delete(dataSource);
  }
}
